/**
 * Reset password — public page reached via the emailed link
 * /reset-password?token=<single-use-token>.
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { scorePassword, validatePassword } from "../lib/passwordStrength";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) toast.error("Missing reset token in URL. Request a fresh link.");
    }, [token]);

    const score = scorePassword(next);
    const matches = next && next === confirm;

    const submit = async (e) => {
        e.preventDefault();
        const err = validatePassword(next);
        if (err) { toast.error(err); return; }
        if (!matches) { toast.error("Passwords don't match."); return; }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: next });
            setDone(true);
            toast.success("Password reset. Please sign in.");
            setTimeout(() => navigate("/login"), 1200);
        } catch (e2) {
            toast.error(apiError(e2));
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
            <div className="max-w-md w-full" data-testid="reset-password-page">
                <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-8">
                    <ArrowLeft className="w-3 h-3" strokeWidth={1.5}/> Back to sign-in
                </Link>
                <div className="bg-card border border-border p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <ShieldCheck className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                        <h1 className="text-base text-foreground">Set a new password</h1>
                    </div>
                    {done ? (
                        <div className="text-xs text-emerald-400" data-testid="reset-success-state">
                            ✓ Password reset. Redirecting to sign-in…
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5 block">New password</label>
                                <div className="relative">
                                    <input type={show ? "text" : "password"} value={next}
                                           onChange={e => setNext(e.target.value)} required autoFocus
                                           data-testid="reset-new-password"
                                           className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 pr-10 text-sm"/>
                                    <button type="button" onClick={() => setShow(s => !s)}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                                        {show ? <EyeOff className="w-4 h-4" strokeWidth={1.5}/> : <Eye className="w-4 h-4" strokeWidth={1.5}/>}
                                    </button>
                                </div>
                                {next && (
                                    <div className="flex items-center gap-2 mt-1.5">
                                        <div className="flex-1 h-1 bg-border overflow-hidden">
                                            <div className="h-full transition-all" style={{width: `${((score.score+1)/6)*100}%`, background: score.color}}/>
                                        </div>
                                        <div className="text-[10px] font-medium" style={{color: score.color}}>{score.label}</div>
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5 block">Confirm new password</label>
                                <input type={show ? "text" : "password"} value={confirm}
                                       onChange={e => setConfirm(e.target.value)} required
                                       data-testid="reset-confirm-password"
                                       className={`w-full bg-background border outline-none px-3 py-2 text-sm ${
                                           confirm ? (matches ? "border-emerald-500/60" : "border-red-500/60") : "border-border focus:border-primary"}`}/>
                            </div>
                            <button type="submit" disabled={loading || !next || !matches || !token}
                                    data-testid="reset-submit"
                                    className="w-full bg-primary text-primary-foreground hover:bg-primary disabled:opacity-40 py-2 text-xs font-medium uppercase tracking-[0.16em]">
                                {loading ? "Setting…" : "Set new password"}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
